/* @jsxImportSource react */
'use client';

import { createContext, type ReactNode, useContext, useId, useMemo } from 'react';

import { Field as FieldBase } from '@base-ui/react/field';
import { IconAlertCircle } from '@tabler/icons-react';
import { type AnyFieldApi, useField } from '@tanstack/react-form';
import { useSelector } from '@tanstack/react-store';

import { cn } from '../../utils/cn';
import { Col } from '../box/box';

export const ATOMS_FIELD_VARIANTS = {} as const;
export const ATOMS_FIELD_DEFAULT_VARIANTS = null;

/** Loose form-instance type — accepts any specific TanStack `FormApi`. */
type AnyFormApi = AnyFieldApi['form'];

/**
 * Carries the form instance from `<Form.Root>` down to `Field` (and the
 * `Form.*` bindings). Defined here because `Field` is the primary consumer —
 * `Form.Root` imports this to provide it.
 */
export const FormContext = createContext<AnyFormApi | null>(null);

/**
 * Read the form provided by `<Form.Root>`, throwing a helpful error if absent.
 * Shared by the connected `Field` and the `Form.*` components.
 */
export function useRequiredFormContext(): AnyFormApi {
  const form = useContext(FormContext);
  if (form == null) {
    throw new Error(
      'A connected <Field name=…> or Form.* component must be rendered inside ' +
        '<Form.Root>. Wrap your form with <Form.Root form={form}>…</Form.Root>.',
    );
  }
  return form;
}

export interface FormControlBaseProps {
  /** Field name in the form's data shape. */
  name: string;
  /**
   * Supporting text rendered beneath the label and above the control — use for
   * hints or extra context. Forwarded to `Field`, which associates it with the
   * control via `aria-describedby` so assistive tech announces it.
   */
  description?: ReactNode;
  /**
   * Marks the field as required. No visual asterisk is shown; a
   * screen-reader-only "(required)" is appended to the label instead. Defaults
   * to the field's required state derived from the form's Zod schema (required
   * unless `.optional()`). Pass explicitly to override.
   */
  required?: boolean;
}

/**
 * Every form-bound control must be labeled: pass a visible `label` (rendered
 * above the control and associated via `htmlFor`), or — for the rare visually
 * unlabeled field — an `aria-label`, rendered as a screen-reader-only label
 * with the same association. Required at the type level so a form control can
 * never ship unnamed.
 */
export type FormControlLabelProps =
  | {
      /** Visible label rendered above the control, associated via `htmlFor`. */
      label: ReactNode;
      'aria-label'?: undefined;
    }
  | {
      label?: undefined;
      /**
       * Accessible label for a visually unlabeled field — rendered as a
       * screen-reader-only `<label htmlFor>` above the control.
       */
      'aria-label': string;
    };

/**
 * Resolve a binding's Field label: the visible `label`, or a screen-reader-only
 * label built from `aria-label`. Either way the control is named through the
 * same `<label htmlFor>` association.
 */
export function fieldLabel(label: ReactNode | undefined, ariaLabel: string | undefined): ReactNode {
  if (label != null) return label;
  if (ariaLabel == null) return undefined;
  return <span className="sr-only">{ariaLabel}</span>;
}

// ---------------------------------------------------------------------------
// Required derivation from the form's Zod schema
// ---------------------------------------------------------------------------

/** Minimal duck-typed shape of a Zod object schema we can introspect. */
interface ZodObjectLike {
  shape?: Record<string, { safeParse?: (value: unknown) => { success: boolean } } | undefined>;
}

/**
 * Structural check for a Zod object schema (exposes `.shape`). Detection is
 * deliberately not an `instanceof` check so it works regardless of which copy
 * of zod built the schema.
 */
function isZodObjectLike(value: unknown): value is ZodObjectLike {
  return typeof value === 'object' && value !== null && 'shape' in value;
}

/**
 * Derive whether `name` is required from the form's Zod schema, so the label's
 * required indicator stays in sync with the schema without a manual prop.
 *
 * The rule is "required unless `.optional()`": a field is optional only when its
 * schema accepts `undefined` (via `.optional()`, `.nullish()`, or `.default()`),
 * so a failing `safeParse(undefined)` means required. This mirrors zod's own
 * documented optionality check (`schema.safeParse(undefined).success`).
 *
 * Only Zod object schemas (which expose `.shape` and per-field `.safeParse`)
 * can be introspected. Detection is structural — not an `instanceof` check — so
 * it works regardless of which copy of zod built the schema. Returns `undefined`
 * when optionality can't be determined (no Zod schema, field not in the shape,
 * or a function/other validator) so the caller falls back to the required
 * default rather than guessing "optional"; pass `required` explicitly to override.
 */
function isRequiredInSchema(form: AnyFormApi, name: string): boolean | undefined {
  const validators = form.options.validators;
  // Use the first Zod-like validator across onChange/onSubmit/onBlur — not just
  // the first *defined* one, so a function validator on one hook doesn't mask a
  // Zod schema on another (which would silently drop the required marker).
  let schema: ZodObjectLike | undefined;
  for (const candidate of [validators?.onChange, validators?.onSubmit, validators?.onBlur]) {
    if (isZodObjectLike(candidate)) {
      schema = candidate;
      break;
    }
  }
  const fieldSchema = schema?.shape?.[name];
  if (typeof fieldSchema?.safeParse !== 'function') return undefined;
  try {
    return !fieldSchema.safeParse(undefined).success;
  } catch {
    return undefined;
  }
}

/**
 * Pulls user-facing error messages off a TanStack field. Handles both raw
 * strings (returned by inline validators) and Zod issue objects (returned by
 * `validators: { onChange: z.object(...) }` in TanStack Form v1).
 */
function extractErrorMessages(field: AnyFieldApi): string[] {
  return field.state.meta.errors
    .map((err: unknown) => {
      if (err == null) return null;
      if (typeof err === 'string') return err;
      if (typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
        return err.message;
      }
      if (typeof err === 'object') {
        // JSON.stringify throws on circular refs and bigint values; fall back to
        // a generic message rather than turning a validation error into a render crash.
        try {
          return JSON.stringify(err);
        } catch {
          return 'Invalid value';
        }
      }
      if (typeof err === 'number' || typeof err === 'boolean' || typeof err === 'bigint') {
        return String(err);
      }
      return null;
    })
    .filter((msg): msg is string => msg != null && msg.length > 0);
}

function normalizeErrors(error: string | string[] | null | undefined): string[] {
  if (!error) return [];
  return (Array.isArray(error) ? error : [error]).filter(
    (msg): msg is string => typeof msg === 'string' && msg.length > 0,
  );
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Bound field state handed to a connected `Field`'s render-prop child. The
 * `Form.*` components use this to wire their specific control; the value/blur
 * mapping is the only per-control-type concern left to them.
 */
export interface FieldControlState {
  /** The field's current value. */
  value: unknown;
  /** Field name — set this on the rendered control. */
  name: string;
  /**
   * DOM id for the control — matches the label's `htmlFor`. Set this as the
   * rendered control's `id` so the visible label is programmatically associated
   * with it (Base UI's `Input`/`Combobox` are not auto-associated).
   */
  id: string;
  /** Update the field value. */
  setValue: (value: unknown) => void;
  /** Mark the field touched — wire to the control's blur. */
  onBlur: () => void;
  /** Whether the control should render its error state (post-submit + invalid). */
  hasError: boolean;
}

/**
 * How the `label` is associated with the control:
 * - `'control'` (default) — rendered as a `<label htmlFor>` that names the control.
 * - `'heading'` — rendered as a non-associated visual heading. Use when the
 *   control supplies its own accessible name (e.g. a Checkbox `caption`), so the
 *   field label doesn't double the control's accessible name.
 */
export type FieldLabelMode = 'control' | 'heading';

interface FieldCommonProps {
  /** Text rendered above the control. */
  label?: ReactNode;
  /** See {@link FieldLabelMode}. Defaults to `'control'`. */
  labelMode?: FieldLabelMode;
  /**
   * Supporting text rendered beneath the label and above the control — use for
   * hints or extra context. Base UI's Field primitive associates it with the
   * control via `aria-describedby`, so assistive tech announces it.
   */
  description?: ReactNode;
  /**
   * DOM id for the control. Used as the label's `htmlFor`; in connected mode it
   * is also handed to the render-prop child via `id` (generated when omitted).
   */
  id?: string;
  /**
   * Marks the field as required (`true`, the default) or optional (`false`).
   * - `true` (default) → screen-reader-only "(required)" appended to the label
   *   (no visual asterisk), so assistive tech still announces it.
   * - `false` → a visible, lighter-grey "(optional)" marker to the right of the
   *   label (`aria-hidden`, so it stays out of the accessible name).
   *
   * In form mode this defaults to the field's required state derived from the
   * form's Zod schema (required unless `.optional()`), falling back to required
   * when it can't be determined; pass explicitly to override.
   */
  required?: boolean;
  className?: string;
}

/**
 * Connected (form) mode: pass the field `name` and a render-prop child. Field
 * reads `<Form.Root>`'s form, subscribes to the field, derives the required
 * state, and renders errors below the control — leaving only the per-type
 * value/blur binding to the child.
 */
export interface FieldConnectedProps extends FieldCommonProps {
  name: string;
  children: (state: FieldControlState) => ReactNode;
}

/**
 * Standalone mode: no form wiring. Pass the control as a normal child plus
 * explicit `error` / `required`. Use when you aren't driving the field with
 * TanStack Form.
 */
export interface FieldStandaloneProps extends FieldCommonProps {
  name?: string;
  /** Error message(s) rendered below the control. Pass a string, an array, or omit. */
  error?: string | string[] | null;
  /** Whether the field is invalid. Defaults to having any `error`. */
  invalid?: boolean;
  children: ReactNode;
}

export type FieldProps = FieldConnectedProps | FieldStandaloneProps;

// ---------------------------------------------------------------------------
// Shared chrome
// ---------------------------------------------------------------------------

function FieldShell({
  label,
  description,
  required,
  errors,
  invalid,
  className,
  name,
  htmlFor,
  labelMode = 'control',
  children,
}: {
  label?: ReactNode;
  description?: ReactNode;
  required?: boolean;
  errors: string[];
  invalid?: boolean;
  className?: string;
  name?: string;
  /** When set, explicitly associates the label with the control of this id. */
  htmlFor?: string;
  labelMode?: FieldLabelMode;
  children: ReactNode;
}) {
  const isInvalid = invalid ?? errors.length > 0;
  // De-duplicate messages (first occurrence wins, order preserved) so each
  // rendered error has a stable, unique key.
  const uniqueErrors = [...new Set(errors)];

  // Shared label content (text + required/optional marker). Rendered either as a
  // `<label htmlFor>` that names the control (default) or, in `'heading'` mode, as
  // a non-associated visual heading — used when the control supplies its own
  // accessible name (e.g. a Checkbox `caption`), so the name isn't doubled.
  const labelContent = label != null && (
    <>
      {label}
      {required === false ? (
        // Visual-only hint: aria-hidden keeps the accessible name clean
        // ("Email", not "Email (optional)") while still showing the marker.
        <span aria-hidden className="text-od-content-tertiary">
          {' '}
          (optional)
        </span>
      ) : (
        // Default (and explicit `true`): required. The marker is
        // screen-reader-only — no visual asterisk.
        <span className="sr-only"> (required)</span>
      )}
    </>
  );

  return (
    <FieldBase.Root
      name={name}
      invalid={isInvalid}
      className={cn('flex w-full flex-col gap-1.5', className)}
    >
      {labelContent &&
        (labelMode === 'heading' ? (
          // Plain heading: no `htmlFor` and no Base UI label registration, so it
          // never contributes to the control's accessible name.
          <span className="text-label-sm text-od-content-primary">{labelContent}</span>
        ) : (
          <FieldBase.Label htmlFor={htmlFor} className="text-label-sm text-od-content-primary">
            {labelContent}
          </FieldBase.Label>
        ))}
      {description != null && (
        <FieldBase.Description className="text-body-sm text-od-content-secondary">
          {description}
        </FieldBase.Description>
      )}
      {children}
      {uniqueErrors.length > 0 && (
        <Col className="gap-0.5">
          {uniqueErrors.map((message) => (
            <FieldBase.Error
              key={message}
              match
              className="flex items-start gap-1 text-body-sm text-od-content-error"
            >
              {/* Top-aligned (so it tracks the first line when the message
                  wraps), nudged 1px to center the icon on that line's glyphs.
                  translate, not margin — atoms forbids margin utilities. */}
              <IconAlertCircle aria-hidden className="h-4 w-4 shrink-0 translate-y-px" />
              {message}
            </FieldBase.Error>
          ))}
        </Col>
      )}
    </FieldBase.Root>
  );
}

const isString = (value: unknown): value is string => typeof value === 'string';

// ---------------------------------------------------------------------------
// Connected (form) mode
// ---------------------------------------------------------------------------

/**
 * Bound field state plus the chrome inputs (`errors`, `isRequired`) a wrapper
 * needs to render its own label/error. Superset of {@link FieldControlState}.
 */
export interface ConnectedFieldState extends FieldControlState {
  /** Error messages to display (gated: empty until the first submit attempt). */
  errors: string[];
  /** Required state — the `required` override, else derived from the Zod schema. */
  isRequired: boolean | undefined;
}

/**
 * Subscribe to a `<Form.Root>` field by name and derive its bound state: value,
 * setter, blur, submit-gated errors, and schema-derived required. The connected
 * `Field` and the `Form.*` bindings share this — including controls like
 * `Radio.Group` that render their own label/error chrome (and so can't be
 * wrapped in `Field` without double-nesting it).
 */
export function useConnectedField(
  name: string,
  opts: {
    /** Required override — wins over the schema-derived state. */
    required?: boolean;
    /** Control id override — wins over the generated one. */
    id?: string;
  } = {},
): ConnectedFieldState {
  const form = useRequiredFormContext();
  const field: AnyFieldApi = useField({ form, name });

  // Owns the control id (honoring a caller-supplied `id`) so a label's `htmlFor`
  // and the child control's `id` are guaranteed to match.
  const generatedId = useId();
  const controlId = opts.id ?? generatedId;

  // Default error-display UX: hide errors until the first submit attempt, then
  // show and live-update them. Driven by the form's `submissionAttempts`.
  const submissionAttempts = useSelector(form.store, (state) => state.submissionAttempts);
  const errors = useMemo(
    () => (submissionAttempts > 0 ? extractErrorMessages(field) : []),
    [submissionAttempts, field],
  );

  // Schema is static, so memoize the probe on [form, name] — runs once on mount.
  const derivedRequired = useMemo(() => isRequiredInSchema(form, name), [form, name]);
  const isRequired = opts.required ?? derivedRequired;

  const fieldName = isString(field.name) ? field.name : '';

  return {
    value: field.state.value,
    name: fieldName,
    id: controlId,
    setValue: (value) => field.handleChange(value),
    onBlur: field.handleBlur,
    hasError: errors.length > 0,
    errors,
    isRequired,
  };
}

function FieldConnected({
  name,
  label,
  labelMode,
  description,
  required,
  className,
  id,
  children,
}: FieldConnectedProps) {
  const state = useConnectedField(name, { required, id });

  return (
    <FieldShell
      label={label}
      labelMode={labelMode}
      description={description}
      required={state.isRequired}
      errors={state.errors}
      className={className}
      name={state.name}
      htmlFor={state.id}
    >
      {children(state)}
    </FieldShell>
  );
}

// ---------------------------------------------------------------------------
// Standalone mode
// ---------------------------------------------------------------------------

function FieldStandalone({
  label,
  labelMode,
  description,
  required,
  error,
  invalid,
  className,
  name,
  id,
  children,
}: FieldStandaloneProps) {
  const errors = normalizeErrors(error);
  return (
    <FieldShell
      label={label}
      labelMode={labelMode}
      description={description}
      required={required}
      errors={errors}
      invalid={invalid}
      className={className}
      name={name}
      htmlFor={id}
    >
      {children}
    </FieldShell>
  );
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

// Connected mode is the render-prop overload: a function child. Standalone's
// `children` is a plain ReactNode (never a function), so this discriminates the
// union cleanly.
function isFieldConnectedProps(props: FieldProps): props is FieldConnectedProps {
  return typeof props.children === 'function';
}

/**
 * Form field wrapper: renders an accessible label above the control and error
 * message(s) below, built on Base UI's Field primitive.
 *
 * Label association: Base UI's `Input`/`Combobox` aren't auto-associated, so in
 * connected mode Field owns a control `id`, sets it as the label's `htmlFor`,
 * and hands it to the render-prop child (`f.id`) — set it on the control. In
 * standalone mode, pass `id` to Field and the same `id` to your control.
 *
 * Two modes:
 * - **Connected** — pass `name` + a render-prop child. Field reads the
 *   `<Form.Root>` form, subscribes to the field, derives the required state from
 *   the Zod schema, and shows errors after the first submit. The `Form.*`
 *   components are thin wrappers over this mode.
 * - **Standalone** — pass a normal child plus explicit `error` / `required`.
 *
 * @example
 * ```tsx
 * // Connected (usually via Form.Input, but available directly):
 * <Field name="email" label="Email">
 *   {(f) => <Input id={f.id} value={f.value as string} onChange={(e) => f.setValue(e.target.value)} error={f.hasError} />}
 * </Field>
 *
 * // Standalone (with a description):
 * <Field label="Email" description="We’ll never share it." id="email" error={errors}>
 *   <Input id="email" type="email" />
 * </Field>
 * ```
 */
export function Field(props: FieldProps) {
  if (isFieldConnectedProps(props)) {
    return <FieldConnected {...props} />;
  }
  return <FieldStandalone {...props} />;
}

Field.displayName = 'Field';
